import { useId, useState } from 'react';
import { Info, ArrowRight, Warning } from '@phosphor-icons/react';
import { resolvePlatform } from '@discovr/types';
import type { ExpectedField, Platform, SheetColumnPreview } from '@discovr/types';
import { Button } from '../../../components/ui/button';
import { SimpleTooltip, IconTooltipButton } from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';
import {
  USERNAME_KEY,
  cleanedFirstValue,
  countLabel,
  groupedFieldOptions,
  handlePreview,
  ignoreSurfacedError,
  isMappingComplete,
  mappingClashes,
  missingRequiredFields,
  pluralWord,
} from './importWizardHelpers';
import type { MappingClash } from './importWizardHelpers';
import { importIdentityWording } from './importIdentityWording';
import {
  NO_MAIN_DELIVERABLE_BODY,
  NO_MAIN_DELIVERABLE_TITLE,
  USAGE_DAYS_NEED_PRICE_COLUMN_MESSAGE,
  isMainDeliverableUnmapped,
  mappingOptionLabel,
  unmappedOptionalFieldLabels,
  usageDaysMappedWithoutPrice,
} from './importMappingRules';
import { WizardFooter } from './ImportWizardFooterSlot';

interface ImportMappingStepProps {
  sheetColumns: SheetColumnPreview[];
  expectedFields: ExpectedField[];
  campaignPlatform: string;
  /** Null when unknown (a resumed batch whose server doesn't send the count). */
  rowCount: number | null;
  busy: boolean;
  onBack: () => void;
  onSubmit: (mapping: Record<string, string>) => Promise<void>;
  /** Notes about the sheet that do not stop the import, e.g. a repeated column
   *  header that was renamed. */
  warnings?: string[];
  /** The mapping submitted before (Back from review, or a resumed draft).
   *  Columns it names start from it; any other column starts from the
   *  server's suggestion. */
  mapping?: Record<string, string>;
}

const NO_PREVIOUS_MAPPING: Record<string, string> = {};
const NO_WARNINGS: string[] = [];
const REPEATED_COLUMN_TITLE = 'Repeated column name.';
const NOT_IMPORTED_LABEL = "Don't import";
const IDENTITY_WARNING_BODY =
  'Every creator needs a handle or profile link so we can find their profile. Pick the column that has it.';
const NOT_IMPORTED_TOOLTIP =
  "Don't import leaves this column out entirely — nothing from it is written, and it won't touch any existing value for creators already on the roster.";
/** Only decides the wording of the deliverable group heading; the field list
 *  itself always comes from the server. Used when the campaign's platform
 *  string isn't one we recognise. */
const FALLBACK_LABEL_PLATFORM: Platform = 'instagram';

/** Sizes the "Maps to" column; the select then fills it. */
const MAPS_TO_COLUMN_CLASS = 'w-2/5';
const SELECT_CLASS_BASE = 'w-full rounded-lg border bg-transparent px-3 py-1.5 text-body-sm-regular';
const SELECT_CLASS_CLASH = `${SELECT_CLASS_BASE} border-destructive text-foreground`;
const SELECT_CLASS_MAPPED = `${SELECT_CLASS_BASE} border-border text-foreground`;
const SELECT_CLASS_NOT_IMPORTED = `${SELECT_CLASS_BASE} border-border text-muted-foreground`;

/** Seeds the mapping from what was submitted before, else the server's
 *  suggestions. Two columns may suggest the same field — that shows as a
 *  clash for the admin to resolve rather than being silently dropped. */
function initialMapping(sheetColumns: SheetColumnPreview[], previous: Record<string, string>): Record<string, string> {
  const initial: Record<string, string> = {};
  for (const col of sheetColumns) {
    initial[col.header] = previous[col.header] ?? (col.suggestedField || '');
  }
  return initial;
}

function selectClassName(hasClash: boolean, hasValue: boolean): string {
  if (hasClash) return SELECT_CLASS_CLASH;
  return hasValue ? SELECT_CLASS_MAPPED : SELECT_CLASS_NOT_IMPORTED;
}

function isUsernameMapped(mapping: Record<string, string>): boolean {
  return Object.values(mapping).includes(USERNAME_KEY);
}

function clashFor(clashes: MappingClash[], header: string): MappingClash | undefined {
  return clashes.find((c) => c.header === header);
}

function identityWarningTitle(columnLabel: string): string {
  return `No column is matched to ${columnLabel}.`;
}

function summaryText(rowCount: number | null, mappedCount: number, notImportedCount: number): string {
  const mapped = `${countLabel(mappedCount, 'column', 'columns')} mapped`;
  const rest = `${mapped} · ${notImportedCount} not imported`;
  if (rowCount === null) return rest;
  return `${rowCount} data ${pluralWord(rowCount, 'row', 'rows')} · ${rest}`;
}

interface FirstValueProps {
  column: SheetColumnPreview;
  field: ExpectedField | undefined;
  campaignPlatform: string;
}

function FirstValue({ column, field, campaignPlatform }: FirstValueProps) {
  const sample = column.sampleValue ?? '';
  if (field?.key === USERNAME_KEY) {
    const handle = handlePreview(sample, campaignPlatform);
    return (
      <span className="inline-flex flex-wrap items-baseline gap-x-1.5">
        <span>{sample || '—'}</span>
        {handle && <span className="font-medium text-foreground">{`→ ${handle}`}</span>}
      </span>
    );
  }
  const shown = field ? cleanedFirstValue(field.key, field.isDeliverable ?? false, sample) : sample;
  return <span>{shown || '—'}</span>;
}

interface MappingWarningProps {
  /** `alert` for a warning that blocks Continue, `status` for a hint. */
  role: 'alert' | 'status';
  title: string;
  body: string;
}

function MappingWarning({ role, title, body }: MappingWarningProps) {
  return (
    <div
      role={role}
      className="flex items-start gap-2 rounded-xl border border-border-warning-default bg-background-warning-light-hover p-3 text-body-sm-regular text-foreground"
    >
      <Warning size={18} weight="fill" className="mt-0.5 shrink-0 text-foreground-warning-default" aria-hidden="true" />
      <p>
        <strong className="font-semibold">{title}</strong> {body}
      </p>
    </div>
  );
}

interface FieldSelectProps {
  header: string;
  value: string;
  clash: MappingClash | undefined;
  groups: ReturnType<typeof groupedFieldOptions>;
  onChange: (header: string, value: string) => void;
}

function FieldSelect({ header, value, clash, groups, onChange }: FieldSelectProps) {
  // Not built from the header: a header with a space would not be a valid id.
  const clashMessageId = useId();
  return (
    <div className="flex flex-col gap-1">
      <select
        aria-label={`Maps to for ${header}`}
        aria-invalid={clash ? true : undefined}
        aria-describedby={clash ? clashMessageId : undefined}
        value={value}
        onChange={(e) => onChange(header, e.target.value)}
        className={selectClassName(clash !== undefined, value !== '')}
      >
        <option value="">{NOT_IMPORTED_LABEL}</option>
        {groups.map((group) => (
          <optgroup key={group.group} label={group.label}>
            {group.fields.map((f) => (
              <option key={f.key} value={f.key}>
                {mappingOptionLabel(f)}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      {clash && (
        <p id={clashMessageId} className="inline-flex items-center gap-1 text-caption-regular text-destructive">
          <Warning size={12} aria-hidden="true" />
          {`Also matched from ${clash.otherHeaders.join(', ')}. Keep one.`}
        </p>
      )}
    </div>
  );
}

export function ImportMappingStep({
  sheetColumns,
  expectedFields,
  campaignPlatform,
  rowCount,
  busy,
  onBack,
  onSubmit,
  warnings = NO_WARNINGS,
  mapping: previousMapping = NO_PREVIOUS_MAPPING,
}: ImportMappingStepProps) {
  const [mapping, setMapping] = useState<Record<string, string>>(() => initialMapping(sheetColumns, previousMapping));

  const platform = resolvePlatform(campaignPlatform) ?? FALLBACK_LABEL_PLATFORM;
  const groups = groupedFieldOptions(expectedFields, platform);
  const clashes = mappingClashes(mapping);
  const usageDaysNeedPriceColumn = usageDaysMappedWithoutPrice(mapping);
  const complete = isMappingComplete(expectedFields, mapping) && !usageDaysNeedPriceColumn;
  const missingOther = missingRequiredFields(expectedFields, mapping).filter((f) => f.key !== USERNAME_KEY);
  const unmappedLabels = unmappedOptionalFieldLabels(expectedFields, mapping);
  const identityColumnLabel = importIdentityWording(campaignPlatform).columnLabel;
  const mappedCount = Object.values(mapping).filter(Boolean).length;
  const notImportedCount = sheetColumns.length - mappedCount;

  const setColumnField = (header: string, value: string) => {
    setMapping((m) => ({ ...m, [header]: value }));
  };

  return (
    <div className="flex flex-col gap-4">
      {!isUsernameMapped(mapping) && (
        <MappingWarning role="alert" title={identityWarningTitle(identityColumnLabel)} body={IDENTITY_WARNING_BODY} />
      )}
      {warnings.map((warning) => (
        <MappingWarning key={warning} role="status" title={REPEATED_COLUMN_TITLE} body={warning} />
      ))}
      {isMainDeliverableUnmapped(expectedFields, mapping) && (
        <MappingWarning role="status" title={NO_MAIN_DELIVERABLE_TITLE} body={NO_MAIN_DELIVERABLE_BODY} />
      )}

      <div className="overflow-hidden rounded-xl border border-border">
        <table className="w-full border-collapse text-left text-[12px]">
          <thead>
            <tr className="bg-muted">
              <th className="border-b border-border px-3 py-2 font-semibold text-muted-foreground">Column in sheet</th>
              <th className="border-b border-border px-3 py-2 font-semibold text-muted-foreground">First value</th>
              <th className="border-b border-border px-1 py-2">
                <span className="sr-only">Becomes</span>
              </th>
              <th className={cn('border-b border-border px-3 py-2 font-semibold text-muted-foreground', MAPS_TO_COLUMN_CLASS)}>
                <span className="inline-flex items-center gap-1.5">
                  Maps to
                  <SimpleTooltip content={NOT_IMPORTED_TOOLTIP}>
                    <IconTooltipButton label="What does Don't import do?">
                      <Info size={12} className="text-muted-foreground" />
                    </IconTooltipButton>
                  </SimpleTooltip>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sheetColumns.map((col) => {
              const current = mapping[col.header] ?? '';
              const mappedField = current ? expectedFields.find((f) => f.key === current) : undefined;
              return (
                <tr key={col.header} className="border-b border-border align-top last:border-b-0">
                  <td className="px-3 py-2 font-mono text-foreground">{col.header}</td>
                  <td className="px-3 py-2 text-muted-foreground">
                    <FirstValue column={col} field={mappedField} campaignPlatform={campaignPlatform} />
                  </td>
                  <td className="px-1 py-2.5">
                    <ArrowRight size={14} className="text-muted-foreground" aria-hidden="true" />
                  </td>
                  <td className="px-3 py-2">
                    <FieldSelect
                      header={col.header}
                      value={current}
                      clash={clashFor(clashes, col.header)}
                      groups={groups}
                      onChange={setColumnField}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {missingOther.length > 0 && (
        <p className="text-body-sm-regular text-destructive">
          Missing required {pluralWord(missingOther.length, 'column', 'columns')}: {missingOther.map((f) => f.label).join(', ')}
        </p>
      )}

      {usageDaysNeedPriceColumn && (
        <p className="text-body-sm-regular text-destructive">{USAGE_DAYS_NEED_PRICE_COLUMN_MESSAGE}</p>
      )}

      {unmappedLabels.length > 0 && (
        <p className="text-body-sm-regular text-muted-foreground">
          {`Not in your sheet: ${unmappedLabels.join(', ')}. Creators keep their current values for these.`}
        </p>
      )}

      <WizardFooter>
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-3">
            <Button type="button" variant="outline" size="lg" disabled={busy} onClick={onBack}>
              Back
            </Button>
            <p className="text-body-sm-regular text-muted-foreground">
              {summaryText(rowCount, mappedCount, notImportedCount)}
            </p>
          </div>
          <Button
            type="button"
            variant="default"
            size="lg"
            loading={busy}
            disabled={!complete}
            onClick={() => { onSubmit(mapping).catch(ignoreSurfacedError); }}
          >
            {busy ? 'Saving…' : 'Continue'}
          </Button>
        </div>
      </WizardFooter>
    </div>
  );
}

